import { Signal } from "signals";
import { HomeCommand } from "slotworks/commands/betbar/home-command";
import { MaxBetMessageCommand } from "slotworks/commands/betbar/max-bet-message-command";
import { HideAllMenusCommand } from "slotworks/commands/menus/hide-all-menus-command";
import { ShowAllMenusCommand } from "slotworks/commands/menus/show-all-menus-command";
import { FindValidTotalStake } from "slotworks/components/betbar/betbar-helpers";
import BetSection from "slotworks/components/betbar/menus/bet-section";
import BetOptions from "slotworks/components/betbar/vo/bet-options";
import Components from "slotworks/components/components";
import SoundToggleComponent from "slotworks/components/sound/sound-toggle-component";
import StepperComponent from "slotworks/components/ui/stepper-component";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import FlexiText from "slotworks/graphics/elements/flexi-text";
import Layers from "slotworks/graphics/layers/layers";
import Currency from "slotworks/i18n/currency";
import { formatMoney } from "slotworks/utils/string-formatters";
import Winnings from "./winnings/winnings";
import SettingsModel from "../../model/settings/settings-model";
import FlexiToggle, { ToggleEvent } from "slotworks/graphics/elements/flexi-toggle";
import GeneralOptions from "slotworks/components/betbar/vo/general-options";
import BetMenuComponent from "slotworks/components/betbar/bet-menu-component";
import slotDefinition from "slotworks/model/slot-definition";
import BetBarResizer from "slotworks/components/betbar/bet-bar-resizer";

export default class BetBarComponent {
    private static PAYTABLE: string = "paytable";

    private static STAKE_DOWN: string = "stake_down";
    private static STAKE_UP: string = "stake_up";
    private static STAKE_VALUE: string = "stake_per_line_value";

    private static LINES_DOWN: string = "lines_down";
    private static LINES_UP: string = "lines_up";
    private static LINES_VALUE: string = "num_lines_value";

    private static STAKE_MENU: string = "stake_menu";
    private static MAX_BET: string = "max";

    private static BALANCE_VALUE: string = "balance_value";

    private static TOTAL_STAKE_VALUE: string = "total_stake_value";
    private static TOTAL_WIN_VALUE: string = "total_win_value";

    private static CURRENT_WIN_VALUE: string = "current_win_value";
    private static FREESPINS_TOTAL_VALUE: string = "freespins_total_value";
    private static FREESPINS_REMAINING_VALUE: string = "freespins_remaining_value";
    private static FREESPINS_X_OF_Y_VALUE: string = "freespins_x_of_y_value";

    private static BET_MENU: string = "betMenu";
    private static HOME: string = "home";

    private static SOUND_ON: string = "soundOn";
    private static SOUND_OFF: string = "soundOff";
    private static SOUND_LOADING: string = "soundLoading";

    private static QUICKSPIN: string = "quickspin";
    private static BETBUTTON: string = "betbtn";

    public onBetUpdated: Signal;
    public onQuickSpinUpdated: Signal;

    public winnings: Winnings;

    private coinSize: number;

    private totalStakeText: FlexiText;
    private totalWinText: FlexiText;
    private balanceText: FlexiText;

    private freespinsTotalText: FlexiText;
    private freespinsRemainingText: FlexiText;
    private freespinsXOfyText: FlexiText;
    private currentWinText: FlexiText;

    private balance: number = 0;
    private totalWin: number = 0;

    private totalFreeSpins: number = 0;
    private remainingFreespins: number = 0;
    private currentWin: number = 0;

    private betOptions: BetOptions;

    private stakeStepper: StepperComponent;
    private lineStepper: StepperComponent;
    private totalStakeStepper: StepperComponent;

    private stakeMenuButton: FlexiButton;
    private maxBetButton: FlexiButton;
    private paytableButton: FlexiButton;
    private betMenuButton: FlexiButton;
    private homeButton: FlexiButton;
    private homeURL: string;
    private quickSpinToggle: FlexiToggle;
    private betButton: FlexiButton;

    private soundToggle: SoundToggleComponent;
    private stakeMenuOpen: boolean = false;
    private stakeMenu: BetSection;

    private isComponentEnabled: boolean = true;

    private betBarResizer: BetBarResizer;

    public constructor() {
        this.onBetUpdated = new Signal();

        this.onQuickSpinUpdated = new Signal();

        this.winnings = new Winnings();

        this.winnings.setup();

        this.betBarResizer = new BetBarResizer();
        this.betBarResizer.setListeners();
    }

    public setType(type: BetBarType) {
        this.destroy();

        Layers.BetBar.setScene(type.toString()).execute();

        this.setupListeners();

        this.winnings.setWinnings(0, 0);
    }

    public destroy() {
        if (this.soundToggle) {
            this.soundToggle.destroy();
        }
        if (this.maxBetButton) {
            this.maxBetButton.off(ButtonEvent.POINTER_DOWN, this.maxBet);
        }
        if (this.betMenuButton) {
            this.betMenuButton.off(ButtonEvent.POINTER_DOWN, Components.betMenu.toggle);
        }
        if (this.homeButton) {
            this.homeButton.off(ButtonEvent.POINTER_UP, this.homePressedHandler);
        }
        if (this.paytableButton && Components.paytable) {
            this.paytableButton.off(ButtonEvent.POINTER_DOWN, Components.paytable.show);
        }
        if (this.stakeMenuButton) {
            this.stakeMenuButton.off(ButtonEvent.POINTER_DOWN, this.toggleStakeMenu);
        }
        if (this.lineStepper) {
            this.lineStepper.destroy();
        }
        if (this.stakeStepper) {
            this.stakeStepper.destroy();
        }
        if (this.totalStakeStepper) {
            this.totalStakeStepper.destroy();
        }
        if(this.quickSpinToggle) {
            this.quickSpinToggle.off(ToggleEvent.CHANGE, this.onQuickSpinChange);
            Components.betMenu.onSettingsOptionsCommitted.remove(this.onOptionsChange);
        }
        if(this.betButton) {
            this.betButton.off(ButtonEvent.POINTER_DOWN, this.onBetButton);
        }
    }

    // Free spins
    public setTotalFreespins(value: number) {
        this.totalFreeSpins = value;
        this.updateFields();
    }

    public setRemainingFreespins(value: number) {
        this.remainingFreespins = value;
        this.updateFields();
    }

    public setCurrentWin(value: number) {
        this.currentWin = value;
        this.updateFields();
    }

    // Universal
    public setEnabled(enable: boolean) {
        if (!enable && this.stakeMenu) {
            this.closeStakeMenu();
        }
        if (this.lineStepper) {
            this.lineStepper.setEnabled(enable);
        }
        if (this.stakeStepper) {
            this.stakeStepper.setEnabled(enable);
        }
        if (this.totalStakeStepper) {
            this.totalStakeStepper.setEnabled(enable);
        }
        if (this.maxBetButton) {
            this.maxBetButton.setEnabled(enable);
        }
        if (this.betMenuButton) {
            this.betMenuButton.setEnabled(enable);
        }
        if (this.stakeMenuButton) {
            this.stakeMenuButton.setEnabled(enable);
        }
        if (Components.header) {
            Components.header.setEnabled(enable);
        }
        if(this.quickSpinToggle) {
            this.quickSpinToggle.setEnabled(enable);
        }
        if(this.betButton) {
            this.betButton.setEnabled(enable);
        }

        this.isComponentEnabled = enable;
    }

    public isEnabled() {
        return this.isComponentEnabled;
    }

    public setBalance(value: number) {
        this.balance = value;
        this.updateFields();
    }

    public setTotalWin(value: number) {
        this.totalWin = value;
        this.updateFields();
    }

    public setHomeURL(url: string) {
        this.homeURL = url;
        this.homeButton = Layers.BetBar.getFlexiButton(BetBarComponent.HOME);
        if (this.homeButton) {
            if (this.homeURL === "close" || this.homeURL === "" || this.homeURL === undefined || this.homeURL === null) {
                this.homeButton.setVisible(false);
            } else {
                this.homeButton.setVisible(true);
            }
        }
    }

    public setBetOptions(betOptions: BetOptions) {
        this.betOptions = betOptions;

        this.destroy();
        this.setupListeners();
    }

    public setLines(value: number) {
        this.lineStepper.setSelectedValue(value);
        this.updateTotalStakeLabel();
    }

    public setStake(value: number) {
        this.stakeStepper.setSelectedValue(value);
        this.updateTotalStakeLabel();
    }

    public setCoinSize(value: number) {
        this.coinSize = value;
        this.updateTotalStakeLabel();
    }

    private setupListeners() {
        const stakeDownButton = Layers.BetBar.getFlexiButton(BetBarComponent.STAKE_DOWN);
        const stakeUpButton = Layers.BetBar.getFlexiButton(BetBarComponent.STAKE_UP);
        const stakeLabel = Layers.BetBar.getFlexiText(BetBarComponent.STAKE_VALUE);

        // Stake stepper
        this.stakeStepper = new StepperComponent(stakeDownButton, stakeUpButton, stakeLabel);
        this.stakeStepper.formatFunction = formatMoney;
        this.setStakeValues(this.betOptions.stakePerLineValues, this.betOptions.stakePerLine);

        // Line stepper
        const lineDownButton = Layers.BetBar.getFlexiButton(BetBarComponent.LINES_DOWN);
        const lineUpButton = Layers.BetBar.getFlexiButton(BetBarComponent.LINES_UP);
        const lineLabel = Layers.BetBar.getFlexiText(BetBarComponent.LINES_VALUE);

        this.lineStepper = new StepperComponent(lineDownButton, lineUpButton, lineLabel);
        this.setLineValues(this.betOptions.lines, this.betOptions.lines);

        // Total stake stepper
        const totalStakeDownBtn = Layers.BetBar.getFlexiButton("total_stake_down");
        const totalStakeUpBtn = Layers.BetBar.getFlexiButton("total_stake_up");

        const totalStakeLabel = Layers.BetBar.getFlexiText("total_stake_value");

        this.totalStakeStepper = new StepperComponent(totalStakeDownBtn, totalStakeUpBtn, totalStakeLabel);
        this.totalStakeStepper.formatFunction = formatMoney;
        this.setTotalStakeValues(this.betOptions.getTotalStakeValues(), this.betOptions.getTotalStake());

        // Stepper listeners
        this.lineStepper.onUpdate.add(this.onLinesUpdated);
        this.stakeStepper.onUpdate.add(this.onStakeUpdated);
        this.totalStakeStepper.onUpdate.add(this.onTotalStakeUpdated);

        // Value fields
        this.balanceText = Layers.BetBar.getFlexiText(BetBarComponent.BALANCE_VALUE);

        this.totalStakeText = Layers.BetBar.getFlexiText(BetBarComponent.TOTAL_STAKE_VALUE);
        this.totalWinText = Layers.BetBar.getFlexiText(BetBarComponent.TOTAL_WIN_VALUE);

        this.currentWinText = Layers.BetBar.getFlexiText(BetBarComponent.CURRENT_WIN_VALUE);
        this.freespinsTotalText = Layers.BetBar.getFlexiText(BetBarComponent.FREESPINS_TOTAL_VALUE);
        this.freespinsRemainingText = Layers.BetBar.getFlexiText(BetBarComponent.FREESPINS_REMAINING_VALUE);
        this.freespinsXOfyText = Layers.BetBar.getFlexiText(BetBarComponent.FREESPINS_X_OF_Y_VALUE);

        // Buttons
        this.maxBetButton = Layers.BetBar.getFlexiButton(BetBarComponent.MAX_BET);
        if (this.maxBetButton) {
            this.maxBetButton.on(ButtonEvent.POINTER_DOWN, this.maxBet);
        }

        this.paytableButton = Layers.BetBar.getFlexiButton(BetBarComponent.PAYTABLE);
        if (this.paytableButton && Components.paytable) {
            this.paytableButton.on(ButtonEvent.POINTER_DOWN, Components.paytable.show);
        }

        this.betMenuButton = Layers.BetBar.getFlexiButton(BetBarComponent.BET_MENU);
        if (this.betMenuButton) {
            this.betMenuButton.on(ButtonEvent.POINTER_DOWN, Components.betMenu.toggle);
        }

        this.homeButton = Layers.BetBar.getFlexiButton(BetBarComponent.HOME);
        if (this.homeButton) {
            if (SettingsModel.homeURL === "close" || SettingsModel.homeURL === "" || SettingsModel.homeURL === undefined || SettingsModel.homeURL === null) {
                this.homeButton.setVisible(false);
            } else {
                this.homeButton.setVisible(true);
            }
            this.homeButton.on(ButtonEvent.POINTER_UP, this.homePressedHandler);
        }

        // Stake popup menu
        this.stakeMenuButton = Layers.BetBar.getFlexiButton(BetBarComponent.STAKE_MENU);
        if (this.stakeMenuButton) {
            if(slotDefinition.isSingleBetLevel()) {
                this.stakeMenuButton.setVisible(false);
            } else {
                this.stakeMenuButton.on(ButtonEvent.POINTER_DOWN, this.toggleStakeMenu);
            }
        }

        // Sound toggle
        const soundOnButton = Layers.BetBar.getFlexiButton(BetBarComponent.SOUND_ON);
        const soundOffButton = Layers.BetBar.getFlexiButton(BetBarComponent.SOUND_OFF);
        const soundLoadingSprite = Layers.BetBar.getFlexiSprite(BetBarComponent.SOUND_LOADING);

        if (soundOnButton && soundOffButton) {
            this.soundToggle = new SoundToggleComponent(soundOnButton, soundOffButton, soundLoadingSprite);
        }

        this.quickSpinToggle = Layers.BetBar.getFlexiToggle(BetBarComponent.QUICKSPIN);
        if(this.quickSpinToggle) {
            if(SettingsModel.isQuickSpinDisabled()) {
                this.quickSpinToggle.setEnabled(false);
                this.quickSpinToggle.setVisible(false);
            } else {
                this.quickSpinToggle.setValue(SettingsModel.getQuickSpin());
                this.quickSpinToggle.on(ToggleEvent.CHANGE, this.onQuickSpinChange);
                Components.betMenu.onSettingsOptionsCommitted.add(this.onOptionsChange);
            }  
        }
        this.betButton = Layers.BetBar.getFlexiButton(BetBarComponent.BETBUTTON);
        if(this.betButton) {
            this.betButton.on(ButtonEvent.POINTER_DOWN, this.onBetButton);
        }
    }

    private toggleStakeMenu = () => {
        if (!this.stakeMenuOpen) {
            this.openStakeMenu();
        } else {
            this.closeStakeMenu();
        }
    }

    private openStakeMenu() {
        HideAllMenusCommand();
        this.stakeMenuOpen = true;

        this.setEnabled(false);

        this.stakeMenuButton.setEnabled(true);
        this.totalStakeStepper.setEnabled(true);

        Layers.BetBarPopups.setScene("betoptions").execute();

        const updateSignal = new Signal();
        this.stakeMenu = new BetSection(updateSignal, this.betOptions, Layers.BetBarPopups);
        updateSignal.add((newOptions: BetOptions) => {
            this.betOptions = newOptions;
            this.updateAndDispatch();
        });

        this.stakeMenu.closeSignal.addOnce(() => {
            this.closeStakeMenu();
        });
    }

    private closeStakeMenu() {
        ShowAllMenusCommand();
        this.stakeMenuOpen = false;

        this.setEnabled(true);
        this.stakeMenu.destroy();
        this.stakeMenu = null;

        Layers.BetBarPopups.defaultScene().execute();
    }

    private setStakeValues(stakeValues: number[], defaultStake: number) {
        this.stakeStepper.setValues(stakeValues);

        this.stakeStepper.setSelectedIndex(stakeValues.indexOf(defaultStake));

        this.updateTotalStakeLabel();
    }

    private setLineValues(lines: number | number[], defaultLines: number) {
        if (typeof lines === "number") {
            const lineArr = [];

            // Create array of lines from 0 - numLines
            for (let i: number = 1; i <= lines; i++) {
                lineArr.push(i);
            }

            lines = lineArr;
        }

        if (lines && lines.length === 0) {
            lines = [defaultLines];
        }

        this.lineStepper.setValues(lines);
        this.lineStepper.setSelectedIndex(defaultLines);

        this.updateTotalStakeLabel();
    }

    private setTotalStakeValues(totalStakeValues: number[], defaultStake: number) {
        this.totalStakeStepper.setValues(totalStakeValues);
        this.totalStakeStepper.setSelectedIndex(totalStakeValues.indexOf(defaultStake));    

        this.updateTotalStakeLabel();
    }

    private updateFields() {
        if (this.balanceText) {
            this.balanceText.setText(Currency.format(this.balance));
            this.betBarResizer.scaleToFitBalanceOnMobile();
        }
        if (this.totalWinText) {
            this.totalWinText.setText(this.totalWin === 0 ? "" : Currency.format(this.totalWin));
        }
        if (this.freespinsTotalText) {
            if (this.totalFreeSpins > 0) {
                this.freespinsTotalText.setText(this.totalFreeSpins.toString());
            } else {
                this.freespinsTotalText.setText("");
            }
        }
        if (this.freespinsRemainingText) {
            this.freespinsRemainingText.setText(this.remainingFreespins.toString());
        }
        if (this.freespinsXOfyText) {
            if (this.totalFreeSpins > 0) {
                this.freespinsXOfyText.setText((this.totalFreeSpins - this.remainingFreespins) + "/" + this.totalFreeSpins);
            } else {
                this.freespinsXOfyText.setText("");
            }
        }
        if (this.currentWinText) {
            this.currentWinText.setText(this.currentWin === 0 ? "" : Currency.format(this.currentWin));
        }
    }

    private homePressedHandler = () => {
        HomeCommand(this.homeURL).execute();
    }

    private onLinesUpdated = (lines: number) => {
        this.betOptions.lines = lines;

        this.updateAndDispatch();
    }

    private onStakeUpdated = (stake: number) => {
        this.betOptions.stakePerLine = stake;

        this.updateAndDispatch();
    }

    private onTotalStakeUpdated = (totalStake: number) => {
        FindValidTotalStake(totalStake, this.betOptions, this.totalStakeStepper, (value: number) => this.onTotalStakeUpdated(value), () => this.updateAndDispatch());
    }

    private updateAndDispatch() {
        this.stakeStepper.setSelectedValue(this.betOptions.stakePerLine);
        this.lineStepper.setSelectedValue(this.betOptions.lines);
        this.totalStakeStepper.setSelectedValue(this.betOptions.getTotalStake());

        if (this.stakeMenu) {
            this.stakeMenu.updateBetOptions(this.betOptions);
        }

        this.updateTotalStakeLabel();

        this.onBetUpdated.dispatch(this.betOptions);
    }

    private updateTotalStakeLabel() {
        if (this.totalStakeText) {
            const stakeValue = Currency.format(Math.round((this.betOptions.getTotalStake()) * 100) / 100);
            this.totalStakeText.setText(stakeValue);
        }
    }

    private maxBet = () => {
        const maxStake = this.betOptions.totalStakeValues[this.betOptions.totalStakeValues.length - 1];

        MaxBetMessageCommand(maxStake);

        this.betOptions.setTotalStake(maxStake);

        this.updateAndDispatch();
    }

    private onQuickSpinChange = () => {
        const quickSpin = this.quickSpinToggle.getValue();
        SettingsModel.setQuickSpin(quickSpin);
        this.onQuickSpinUpdated.dispatch(quickSpin);
        const options = SettingsModel.generateGeneralOptions();
        Components.betMenu.setGeneralOptions(options);
    }

    private onOptionsChange = (newGeneralOptions: GeneralOptions) => {
        this.quickSpinToggle.setValue(newGeneralOptions.quickSpin);
    }

    private onBetButton = () => {
        Components.betMenu.show(BetMenuComponent.BET);
    }
}

export class BetBarType {

    public static DEFAULT: BetBarType = new BetBarType("default");
    public static FREESPINS: BetBarType = new BetBarType("freespins");

    constructor(private type: string) {
    }

    public toString(): string {
        return this.type;
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/components/betbar/bet-bar-component.ts